import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Trophy, TrendingUp, Users, RotateCcw, DollarSign, Zap, Sparkles } from "lucide-react";

const rateClass = (r) => r < 3 ? "text-emerald-600" : r < 8 ? "text-amber-600" : "text-red-600";

export default function MyDashboard() {
  const { user, currentCompany } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/reports/my-stats");
        setData(d);
      } catch (e) { toast.error(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-center text-muted-foreground py-16 text-sm">جاري التحميل...</div>;
  if (!data) return null;

  const cur = currentCompany?.base_currency || "SYP";
  return (
    <div className="space-y-6" data-testid="my-dashboard-page">
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-lg">
          <Trophy className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">أهلاً {user?.name} 👋</h1>
          <p className="text-sm text-muted-foreground">لوحة أدائك الشخصية · {data.period.from} → {data.period.to}</p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4 border-l-4 border-l-blue-500" data-testid="kpi-today">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><Zap className="w-3.5 h-3.5 text-blue-500" /> مبيعات اليوم</div>
          <div className="text-2xl font-bold text-blue-700 mt-1 tabular-nums">{data.today_total.toLocaleString("ar-EG")}</div>
          <div className="text-[10px] text-muted-foreground mt-1">{data.today_count} فاتورة</div>
        </Card>
        <Card className="p-4 border-l-4 border-l-emerald-500" data-testid="kpi-mtd">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><TrendingUp className="w-3.5 h-3.5 text-emerald-500" /> إجمالي الشهر</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1 tabular-nums">{data.net_total.toLocaleString("ar-EG")}</div>
          <div className="text-[10px] text-muted-foreground mt-1">صافي بعد المرتجعات</div>
        </Card>
        <Card className="p-4 border-l-4 border-l-violet-500" data-testid="kpi-commission">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><DollarSign className="w-3.5 h-3.5 text-violet-500" /> عمولتك المتوقعة</div>
          <div className="text-2xl font-bold text-violet-700 mt-1 tabular-nums">{data.commission.toLocaleString("ar-EG")}</div>
          <div className="text-[10px] text-muted-foreground mt-1">بمعدل {data.commission_rate}%</div>
        </Card>
        <Card className={`p-4 border-l-4 ${data.return_rate < 3 ? "border-l-emerald-500" : data.return_rate < 8 ? "border-l-amber-500" : "border-l-red-500"}`} data-testid="kpi-return-rate">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><RotateCcw className="w-3.5 h-3.5" /> معدل إرجاعك</div>
          <div className={`text-2xl font-bold mt-1 tabular-nums ${rateClass(data.return_rate)}`}>{data.return_rate.toFixed(1)}%</div>
          <div className="text-[10px] text-muted-foreground mt-1">{data.returns_count} مرتجع من {data.sales_count} فاتورة</div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Daily sales area chart */}
        <Card className="p-4 lg:col-span-2 min-h-[320px]">
          <h2 className="font-heading font-bold mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-500" /> مبيعاتك اليومية (آخر 14 يوم)
          </h2>
          {data.daily_series.length === 0 ? (
            <div className="text-center text-muted-foreground py-16 text-sm">لا توجد مبيعات في هذه الفترة</div>
          ) : (
            <div className="h-64" data-testid="daily-sales-chart">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <AreaChart data={data.daily_series}>
                  <defs>
                    <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v.toLocaleString("ar-EG")} />
                  <Tooltip formatter={(v) => `${v.toLocaleString("ar-EG")} ${cur}`} />
                  <Area type="monotone" dataKey="total" stroke="#3B82F6" strokeWidth={2} fill="url(#salesFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        {/* Top customers */}
        <Card className="p-4 min-h-[320px]">
          <h2 className="font-heading font-bold mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-violet-500" /> أعلى 5 عملاء
          </h2>
          {data.top_customers.length === 0 ? (
            <div className="text-center text-muted-foreground py-16 text-sm">—</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-start">العميل</TableHead>
                  <TableHead className="text-end">فواتير</TableHead>
                  <TableHead className="text-end">الإجمالي</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.top_customers.map((c, i) => (
                  <TableRow key={c.party_id} data-testid={`top-cust-${i}`}>
                    <TableCell className="font-semibold text-xs flex items-center gap-1.5">
                      <span className="text-[9px] px-1 rounded bg-violet-100 text-violet-800 font-mono">#{i + 1}</span>
                      {c.party_name}
                    </TableCell>
                    <TableCell className="text-end text-xs tabular-nums">{c.count}</TableCell>
                    <TableCell className="text-end text-xs tabular-nums font-bold text-emerald-700">{c.total.toLocaleString("ar-EG")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      {/* Motivational footer */}
      <Card className="p-4 bg-gradient-to-r from-blue-50 to-violet-50 dark:from-blue-950/20 dark:to-violet-950/20 border-dashed">
        <div className="flex items-center gap-3">
          <Sparkles className="w-6 h-6 text-amber-500 shrink-0" />
          <div className="text-sm">
            {data.return_rate < 3 && <span>أداء ممتاز! ابقَ محافظاً على هذا المعدل وستحقق عمولة أعلى الشهر القادم.</span>}
            {data.return_rate >= 3 && data.return_rate < 8 && <span>أداء جيد. حاول تقليل الإرجاعات لرفع عمولتك — راجع بيانات المنتج مع العميل قبل الإغلاق.</span>}
            {data.return_rate >= 8 && <span>⚠️ معدل إرجاعك مرتفع — راجع طريقة العرض والوعود مع العملاء لتحسين الأداء.</span>}
          </div>
        </div>
      </Card>
    </div>
  );
}
